import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiStar, FiPlay, FiFilm, FiHeart, FiTrendingUp } from 'react-icons/fi';
import { trackAnimeView, trackPageView } from '../../../utils/analytics';
import SEOHead from '../../SEO/SEOHead';
import './AnimeDetailPage.css';

const toSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const AnimeDetailPage = () => {
    const { id: rawId } = useParams();
    const animeId = rawId.split('-')[0]; // extract numeric ID from "457-mushishi"
    const navigate = useNavigate();
    const [anime, setAnime] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandSynopsis, setExpandSynopsis] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        setAnime(null);
        setCharacters([]);
        setReviews([]);
        setRecommendations([]);
        setExpandSynopsis(false);

        const fetchAll = async () => {
            try {
                const res = await fetch(`https://api.jikan.moe/v4/anime/${animeId}/full`);
                const data = await res.json();
                setAnime(data.data);
                setLoading(false);

                // Track this anime view in GA with the actual anime title
                if (data.data?.title) {
                    const pageTitle = `ChibiDash | ${data.data.title}`;
                    document.title = pageTitle;
                    trackPageView(`/animehub/anime/${rawId}`, pageTitle);
                    trackAnimeView(animeId, data.data.title);
                }

                await new Promise(r => setTimeout(r, 400));
                const charRes = await fetch(`https://api.jikan.moe/v4/anime/${animeId}/characters`);
                const charData = await charRes.json();
                setCharacters(charData.data || []);

                await new Promise(r => setTimeout(r, 400));
                const revRes = await fetch(`https://api.jikan.moe/v4/anime/${animeId}/reviews`);
                const revData = await revRes.json();
                setReviews(revData.data || []);

                await new Promise(r => setTimeout(r, 400));
                const recRes = await fetch(`https://api.jikan.moe/v4/anime/${animeId}/recommendations`);
                const recData = await recRes.json();
                setRecommendations(recData.data || []);
            } catch (error) {
                console.error('Error fetching anime details:', error);
                setLoading(false);
            }
        };

        fetchAll();
    }, [animeId]);

    if (loading) {
        return (
            <div className="anime-detail-loading">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading anime details...</p>
            </div>
        );
    }

    if (!anime) return null;

    const statusClass = anime.status === 'Currently Airing' ? 'airing'
        : anime.status === 'Not yet aired' ? 'upcoming' : 'finished';

    const studio = anime.studios?.length > 0 ? anime.studios[0].name : 'Unknown';
    const season = anime.season
        ? `${anime.season.charAt(0).toUpperCase() + anime.season.slice(1)} ${anime.year || ''}`
        : anime.year || '';

    return (
        <div className="anime-detail-page">
            <SEOHead
                title={`${anime.title} - Anime Details, Reviews & Characters | ChibiDash`}
                description={anime.synopsis ? anime.synopsis.substring(0, 160) : `${anime.title} - Watch details, characters, reviews and recommendations on ChibiDash Anime Hub.`}
                path={`/animehub/anime/${rawId}`}
                image={anime.images?.jpg?.large_image_url}
                type="article"
                keywords={`${anime.title}, ${anime.title_english || ''}, anime, ${anime.genres?.map(g => g.name).join(', ') || ''}, anime review, anime characters`}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "TVSeries",
                    "name": anime.title,
                    "alternateName": anime.title_english || undefined,
                    "description": anime.synopsis?.substring(0, 300),
                    "image": anime.images?.jpg?.large_image_url,
                    "genre": anime.genres?.map(g => g.name),
                    "aggregateRating": anime.score ? {
                        "@type": "AggregateRating",
                        "ratingValue": anime.score,
                        "bestRating": 10,
                        "ratingCount": anime.scored_by || 0
                    } : undefined,
                    "numberOfEpisodes": anime.episodes || undefined,
                    "productionCompany": studio !== 'Unknown' ? { "@type": "Organization", "name": studio } : undefined
                }}
            />
            {/* Hero Banner */}
            <div className="anime-detail-hero" style={{
                backgroundImage: `url(${anime.images?.jpg?.large_image_url || anime.images?.jpg?.image_url})`
            }}>
                <div className="anime-detail-hero-overlay">
                    <button className="anime-detail-back" onClick={() => navigate('/animehub')}>
                        <FiArrowLeft /> Back to Anime Hub
                    </button>
                    <div className="anime-detail-hero-content">
                        <img
                            src={anime.images?.jpg?.large_image_url || anime.images?.jpg?.image_url}
                            alt={anime.title}
                            className="anime-detail-poster"
                        />
                        <div className="anime-detail-hero-info">
                            <h1 className="anime-detail-title">{anime.title}</h1>
                            {anime.title_english && anime.title_english !== anime.title && (
                                <p className="anime-detail-title-en">{anime.title_english}</p>
                            )}
                            <div className="anime-detail-badges">
                                {anime.type && <span className="detail-badge detail-badge-type">{anime.type}</span>}
                                <span className={`detail-badge detail-badge-status ${statusClass}`}>
                                    {anime.status}
                                </span>
                                {anime.rating && <span className="detail-badge detail-badge-rating">{anime.rating}</span>}
                            </div>
                            <div className="anime-detail-stats">
                                <div className="detail-stat">
                                    <FiStar className="detail-stat-icon gold" />
                                    <span className="detail-stat-value">{anime.score || 'N/A'}</span>
                                    <span className="detail-stat-label">
                                        {anime.scored_by ? `${(anime.scored_by / 1000).toFixed(0)}K votes` : 'Score'}
                                    </span>
                                </div>
                                <div className="detail-stat">
                                    <FiTrendingUp className="detail-stat-icon" />
                                    <span className="detail-stat-value">#{anime.rank || 'N/A'}</span>
                                    <span className="detail-stat-label">Rank</span>
                                </div>
                                <div className="detail-stat">
                                    <FiHeart className="detail-stat-icon pink" />
                                    <span className="detail-stat-value">#{anime.popularity || 'N/A'}</span>
                                    <span className="detail-stat-label">Popularity</span>
                                </div>
                                <div className="detail-stat">
                                    <FiFilm className="detail-stat-icon" />
                                    <span className="detail-stat-value">{anime.episodes || '?'}</span>
                                    <span className="detail-stat-label">Episodes</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Info Cards */}
            <div className="anime-detail-body">
                <div className="anime-detail-grid">
                    {/* Left - Main Content */}
                    <div className="anime-detail-main">
                        {/* Synopsis */}
                        <section className="detail-section">
                            <h2 className="detail-section-title">Synopsis</h2>
                            <p className={`detail-synopsis ${expandSynopsis ? 'expanded' : ''}`}>
                                {anime.synopsis || 'No synopsis available.'}
                            </p>
                            {anime.synopsis && anime.synopsis.length > 300 && (
                                <button
                                    className="synopsis-toggle"
                                    onClick={() => setExpandSynopsis(!expandSynopsis)}
                                >
                                    {expandSynopsis ? 'Show Less' : 'Read More'}
                                </button>
                            )}
                        </section>

                        {/* Streaming Links */}
                        {anime.streaming && anime.streaming.length > 0 && (
                            <section className="detail-section">
                                <h2 className="detail-section-title">Where to Watch</h2>
                                <div className="detail-streaming-links">
                                    {anime.streaming.map((platform, i) => (
                                        <a key={i} href={platform.url} target="_blank" rel="noopener noreferrer" className="detail-stream-btn">
                                            <FiPlay /> {platform.name}
                                        </a>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Trailer */}
                        {anime.trailer?.embed_url && (
                            <section className="detail-section">
                                <h2 className="detail-section-title">Trailer</h2>
                                <div className="detail-trailer-wrapper">
                                    <iframe
                                        src={anime.trailer.embed_url}
                                        title="Trailer"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="detail-trailer"
                                    />
                                </div>
                            </section>
                        )}

                        {/* Characters */}
                        {characters.length > 0 && (
                            <section className="detail-section">
                                <h2 className="detail-section-title">Characters</h2>
                                <div className="detail-characters-grid">
                                    {characters.slice(0, 12).map((c) => (
                                        <div key={c.character.mal_id} className="detail-character-card">
                                            <img
                                                src={c.character.images?.jpg?.image_url}
                                                alt={c.character.name}
                                                className="detail-character-img"
                                            />
                                            <div className="detail-character-info">
                                                <span className="detail-character-name">{c.character.name}</span>
                                                <span className={`detail-character-role ${c.role.toLowerCase()}`}>{c.role}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Reviews */}
                        {reviews.length > 0 && (
                            <section className="detail-section">
                                <h2 className="detail-section-title">Reviews</h2>
                                <div className="detail-reviews-list">
                                    {reviews.slice(0, 5).map((review, i) => (
                                        <div key={i} className="detail-review-card">
                                            <div className="detail-review-header">
                                                <img
                                                    src={review.user?.images?.jpg?.image_url}
                                                    alt={review.user?.username}
                                                    className="detail-review-avatar"
                                                />
                                                <div>
                                                    <span className="detail-review-user">{review.user?.username}</span>
                                                    <span className="detail-review-score">{review.score}/10</span>
                                                </div>
                                            </div>
                                            <p className="detail-review-text">
                                                {review.review?.substring(0, 250)}...
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Similar Anime */}
                        {recommendations.length > 0 && (
                            <section className="detail-section">
                                <h2 className="detail-section-title">More Like This</h2>
                                <div className="detail-similar-grid">
                                    {recommendations.slice(0, 12).map((rec) => (
                                        <Link
                                            key={rec.entry.mal_id}
                                            to={`/animehub/anime/${rec.entry.mal_id}-${toSlug(rec.entry.title)}`}
                                            className="detail-similar-card"
                                        >
                                            <img
                                                src={rec.entry.images?.jpg?.image_url}
                                                alt={rec.entry.title}
                                                className="detail-similar-img"
                                            />
                                            <span className="detail-similar-title">{rec.entry.title}</span>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Right Sidebar - Info */}
                    <aside className="anime-detail-sidebar">
                        <div className="detail-info-card">
                            <h3 className="detail-info-card-title">Information</h3>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Type</span>
                                <span className="detail-info-value">{anime.type || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Episodes</span>
                                <span className="detail-info-value">{anime.episodes || 'Unknown'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Status</span>
                                <span className="detail-info-value">{anime.status || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Aired</span>
                                <span className="detail-info-value">{anime.aired?.string || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Season</span>
                                <span className="detail-info-value">{season || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Studio</span>
                                <span className="detail-info-value">{studio}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Source</span>
                                <span className="detail-info-value">{anime.source || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Duration</span>
                                <span className="detail-info-value">{anime.duration || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Rating</span>
                                <span className="detail-info-value">{anime.rating || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Favorites</span>
                                <span className="detail-info-value">{anime.favorites?.toLocaleString() || 'N/A'}</span>
                            </div>
                            <div className="detail-info-row">
                                <span className="detail-info-label">Members</span>
                                <span className="detail-info-value">{anime.members?.toLocaleString() || 'N/A'}</span>
                            </div>
                        </div>

                        {/* Genres */}
                        {anime.genres?.length > 0 && (
                            <div className="detail-info-card">
                                <h3 className="detail-info-card-title">Genres</h3>
                                <div className="detail-genres-wrap">
                                    {anime.genres.map(g => (
                                        <span key={g.mal_id} className="detail-genre-tag">{g.name}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Themes */}
                        {anime.themes?.length > 0 && (
                            <div className="detail-info-card">
                                <h3 className="detail-info-card-title">Themes</h3>
                                <div className="detail-genres-wrap">
                                    {anime.themes.map(t => (
                                        <span key={t.mal_id} className="detail-genre-tag detail-theme-tag">{t.name}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* External Links */}
                        {anime.url && (
                            <div className="detail-info-card">
                                <h3 className="detail-info-card-title">External</h3>
                                <a href={anime.url} target="_blank" rel="noopener noreferrer" className="detail-external-link">
                                    MyAnimeList Page
                                </a>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default AnimeDetailPage;
